import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaUpload, FaDownload, FaArrowLeft, FaCog, FaCamera, FaEye, FaTimes } from 'react-icons/fa';

const MessageScreenGrab = ({ onBack }) => {
  // State management - load from localStorage
  const [backgroundImage, setBackgroundImage] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabImages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.backgroundImage || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [foregroundImage, setForegroundImage] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabImages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.foregroundImage || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [conversationData, setConversationData] = useState('');
  const [parsedMessages, setParsedMessages] = useState([]);
  const [isAnimating, setIsAnimating] = useState(true); // Auto-start animation
  const [isExporting, setIsExporting] = useState(false);
  const [showUI, setShowUI] = useState(true); // Start with UI open
  const [showBackground, setShowBackground] = useState(true);
  const [showForeground, setShowForeground] = useState(true);
  const [messageAnimationKey, setMessageAnimationKey] = useState(0); // For looping messages
  
  // ===== AUTOMATIC FILE DISCOVERY SYSTEM =====
  // Automatically discovers all PNG files from /bg and /fg folders
  // Converts kebab-case filenames to Normal Case labels
  // No manual file listing needed - just add files to the folders!
  
  // Convert kebab-case filename to Normal Case label
  const kebabToNormalCase = (filename) => {
    return filename
      .replace(/\.[^/.]+$/, '') // Remove file extension
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Use manual file discovery to avoid console spam
  const getBackgroundFiles = () => {
    // Manual list based on known files from folder scan
    const knownBackgroundFiles = [
      'construction-site.png',
      'wood-table.png',
      'marble.png'
    ];
    
    return knownBackgroundFiles.map(filename => ({
      name: kebabToNormalCase(filename),
      path: `/bg/${filename}`
    }));
  };

  // Use manual file discovery to avoid console spam
  const getForegroundFiles = () => {
    // Manual list based on known files from folder scan
    const knownForegroundFiles = [
      'palm.png'
    ];
    
    return knownForegroundFiles.map(filename => ({
      name: kebabToNormalCase(filename),
      path: `/fg/${filename}`
    }));
  };

  // Generate arrays with auto-discovered files
  const availableBackgrounds = getBackgroundFiles();
  const availableForegrounds = getForegroundFiles();
  const [selectedBackground, setSelectedBackground] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabImages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.selectedBackground || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [selectedForeground, setSelectedForeground] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabImages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.selectedForeground || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [useCustomBackground, setUseCustomBackground] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabImages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.useCustomBackground || false;
      } catch (e) {
        return false;
      }
    }
    return false;
  });
  const [useCustomForeground, setUseCustomForeground] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabImages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.useCustomForeground || false;
      } catch (e) {
        return false;
      }
    }
    return false;
  });
  const [showBgDropdown, setShowBgDropdown] = useState(false);
  const [showFgDropdown, setShowFgDropdown] = useState(false);
  
  // Position and rotation state for drag and drop - load from localStorage
  const [elementPositions, setElementPositions] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabPositions');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved positions');
      }
    }
    return {
      phone: { x: 0, y: 0 },
      background: { x: 0, y: 0, rotation: 0 },
      foreground: { x: -50, y: -50, rotation: 15 }
    };
  });
  
  // UI panel position state
  const [uiPanelPosition, setUiPanelPosition] = useState({ x: 0, y: 0 });
  

  
  // Conversation editing state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingConversationIndex, setEditingConversationIndex] = useState(0);
  const [editableConversations, setEditableConversations] = useState([]);
  
  // Palm swaying animation state - load from localStorage
  const [palmSwayIntensity, setPalmSwayIntensity] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabSettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.palmSwayIntensity || 0;
      } catch (e) {
        return 0;
      }
    }
    return 0;
  });
  const [palmAnchorPoint, setPalmAnchorPoint] = useState(() => {
    const saved = localStorage.getItem('messageScreenGrabSettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.palmAnchorPoint || { x: 85, y: 85 };
      } catch (e) {
        return { x: 85, y: 85 };
      }
    }
    return { x: 85, y: 85 };
  });
  const [palmSwayTransform, setPalmSwayTransform] = useState({ rotation: 0, x: 0, y: 0 });
  
  // Typing indicator state
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [typingForMessageIndex, setTypingForMessageIndex] = useState(-1);
  
  // Reaction state
  const [showReaction, setShowReaction] = useState(false);
  
  // Anchor point visualization
  const [showAnchorPoint, setShowAnchorPoint] = useState(false);
  const [anchorAdjustTimeout, setAnchorAdjustTimeout] = useState(null);
  const [isAnchorEditMode, setIsAnchorEditMode] = useState(false);
  
  // Avatar cropping state
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [avatarCropImage, setAvatarCropImage] = useState(null);
  const [cropScale, setCropScale] = useState(1);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const avatarCropCanvasRef = useRef(null);
  
  // Load settings from localStorage with defaults
  const loadSettings = () => {
    const saved = localStorage.getItem('messageScreenGrabSettings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved settings');
      }
    }
    return {
      cameraShake: 50,
      messageDelay: 2500,
      typingDuration: 1800,
      backgroundScale: 1.1,
      backgroundBlur: 0,
      foregroundScale: 2.5,
      foregroundBlur: 6,
      foregroundOpacity: 0.7,
      foregroundDepth: 50,
      foregroundInFront: true,
      iphoneBorderColor: '#000000',
      useIOSBlue: true,
      palmSwayIntensity: 0,
      palmAnchorPoint: { x: 85, y: 85 }
    };
  };

  const [settings, setSettings] = useState(() => {
    const loaded = loadSettings();
    return {
      cameraShake: loaded.cameraShake || 50,
      messageDelay: loaded.messageDelay || 2500,
      typingDuration: loaded.typingDuration || 1800,
      backgroundScale: loaded.backgroundScale || 1.1,
      backgroundBlur: loaded.backgroundBlur || 0,
      foregroundScale: loaded.foregroundScale || 2.5,
      foregroundBlur: loaded.foregroundBlur || 6,
      foregroundOpacity: loaded.foregroundOpacity !== undefined ? loaded.foregroundOpacity : 0.7,
      foregroundDepth: loaded.foregroundDepth || 50,
      foregroundInFront: loaded.foregroundInFront !== undefined ? loaded.foregroundInFront : true
    };
  });
  
  // iPhone border color
  const [iphoneBorderColor, setIphoneBorderColor] = useState(() => {
    const loaded = loadSettings();
    return loaded.iphoneBorderColor || '#000000';
  });
  
  // Message bubble color (true = iOS blue, false = green)
  const [useIOSBlue, setUseIOSBlue] = useState(() => {
    const loaded = loadSettings();
    return loaded.useIOSBlue !== undefined ? loaded.useIOSBlue : true;
  });
  
  // Bot customization (name and avatar)
  const [botName, setBotName] = useState(() => {
    const loaded = loadSettings();
    return loaded.botName || 'Robin';
  });
  const [avatarImage, setAvatarImage] = useState(() => {
    // Load avatar from separate localStorage key to avoid quota issues
    try {
      const savedAvatar = localStorage.getItem('messageScreenGrabAvatar');
      if (savedAvatar) return savedAvatar;
    } catch (e) {
      console.error('Error loading saved avatar:', e);
    }
    
    // Fallback to old location for backwards compatibility
    const loaded = loadSettings();
    return loaded.avatarImage || '/goofer.gif';
  });
  const avatarInputRef = useRef(null);
  
  // Camera shake animation
  const [shakeTransform, setShakeTransform] = useState({ x: 0, y: 0, rotation: 0 });
  const [foregroundShakeTransform, setForegroundShakeTransform] = useState({ x: 0, y: 0 });
  const [animationHeartbeat, setAnimationHeartbeat] = useState(0);

  // Refs
  const containerRef = useRef(null);
  const messagesRef = useRef(null);
  const bgInputRef = useRef(null);
  const fgInputRef = useRef(null);
  const exportCanvasRef = useRef(null);
  const sceneContainerRef = useRef(null);
  const shakeAnimationRef = useRef(null);
  const palmSwayAnimationRef = useRef(null);

  // Sample conversation data - Event conversations
  const conversationVariations = [
    // Banff Pass - Short & conversational
    `{
      "messages": [
        {
          "type": "sent",
          "text": "Do we get a free Banff national pass?",
          "timestamp": "12:18 PM"
        },
        {
          "type": "received",
          "text": "Unfortunately no, park passes aren't included with attendance.",
          "timestamp": "12:18 PM",
          "sender": "Robin"
        },
        {
          "type": "sent",
          "text": "Is there wifi?",
          "timestamp": "9:09 AM"
        },
        {
          "type": "received",
          "text": "Yes! WiFi is available at The Gathering venues.\n\nNetwork: Gathering2025\nPassword: TGS_2025 📶",
          "timestamp": "9:09 AM",
          "sender": "Robin"
        }
      ]
    }`,
    
      // What's Blocking Me? - MEP Contractor
      `{
      "messages": [
        {
          "type": "sent",
          "text": "What's blocking our MEP rough-ins from starting?",
          "timestamp": "10:15 AM"
        },
        {
          "type": "received",
          "text": "MEP rough-ins depend on two tasks: 1) Insulated metal wall panels (CSMS) - must complete by Jun 19, 2026. 2) Underslab plumbing (Bornhorst) - must complete by Apr 24, 2026. CSMS panels are the critical path item. 🔧",
          "timestamp": "10:16 AM",
          "sender": "Robin"
        },
        {
          "type": "sent",
          "text": "When can we mobilize?",
          "timestamp": "10:17 AM"
        },
        {
          "type": "received",
          "text": "MEP rough-ins (Task 29) start May 11, 2026 and run for 12 weeks through July 31. You'll be coordinating with Bornhorst (mechanical), BDN (electrical), and Alico (HVAC). Site super Jennifer Walsh will confirm 2 weeks before. ⚡",
          "timestamp": "10:18 AM",
          "sender": "Robin"
        }
      ]
    }`,
    
      // Active Pour - Site Access
      `{
      "messages": [
        {
          "type": "sent",
          "text": "Can I access Bay A right now?",
          "timestamp": "1:50 PM"
        },
        {
          "type": "received",
          "text": "🚨 Bay A-F is RESTRICTED during active concrete pour (1:45-2:45 PM). Pour #3 in progress. Access resumes after finishing phase. Emergency? Contact Jennifer Walsh: (204) 555-0182",
          "timestamp": "1:50 PM",
          "sender": "Robin"
        },
        {
          "type": "sent",
          "text": "Got it. Where should I wait?",
          "timestamp": "1:51 PM"
        },
        {
          "type": "received",
          "text": "Staging area in Bay H is clear. You can wait there or in the site office trailer. Pour should wrap by 2:45 PM, then 30 min for initial finishing. I'll update you when Bay A reopens! 🏗️",
          "timestamp": "1:52 PM",
          "sender": "Robin"
        }
      ]
    }`,
    
      // Contractor Lookup
      `{
      "messages": [
        {
          "type": "sent",
          "text": "Who's handling the plumbing work on this job?",
          "timestamp": "11:22 AM"
        },
        {
          "type": "received",
          "text": "Bornhorst handles plumbing: Task 28 (Underslab trenching, Apr 6-24, 2026) and Task 29 (MEP rough-ins, May 11 - Jul 31, 2026). They're coordinating with BDN (electrical) and Alico (mechanical) for the MEP package. 🔧",
          "timestamp": "11:23 AM",
          "sender": "Robin"
        },
        {
          "type": "sent",
          "text": "Thanks! Do you have their contact info?",
          "timestamp": "11:24 AM"
        },
        {
          "type": "received",
          "text": "Contact John Bornhorst at (204) 555-4006. For site-specific coordination, reach out to Jennifer Walsh (Site Super) at (204) 555-0182. She's your main point of contact for all trade coordination! 📞",
          "timestamp": "11:25 AM",
          "sender": "Robin"
        }
      ]
    }`,
    
      // Full Contractor Schedule
      `{
      "messages": [
        {
          "type": "sent",
          "text": "Robin, can you show me Maple Leaf's full schedule?",
          "timestamp": "2:30 PM"
        },
        {
          "type": "received",
          "text": "Maple Leaf Construction has 10 tasks scheduled:\n\n✅ Completed:\n- Site stripping (Aug 25-29, 2025)\n- Sub-base install (Sep 2-12, 2025)\n\n🔨 In Progress:\n- Site services (Oct 27, 2025 - Jan 2, 2026)\n\n📅 Upcoming:\n- Ramp excavation (Apr 27 - May 22, 2026)\n- Concrete ramps (Jun 29 - Aug 7, 2026)\n- Sidewalks (Jun 29 - Jul 10, 2026)\n- Asphalt paving (Aug 10-21, 2026)\n- Line painting (Aug 24-28, 2026)",
          "timestamp": "2:31 PM",
          "sender": "Robin"
        },
        {
          "type": "sent",
          "text": "That's super helpful, thanks!",
          "timestamp": "2:32 PM"
        },
        {
          "type": "received",
          "text": "Happy to help! Need anything else about the schedule or site logistics? I'm here 24/7! 🚧",
          "timestamp": "2:33 PM",
          "sender": "Robin"
        }
      ]
    }`,
    
      // Safety/PPE Question
      `{
      "messages": [
        {
          "type": "sent",
          "text": "What PPE do I need for concrete work today?",
          "timestamp": "8:15 AM"
        },
        {
          "type": "received",
          "text": "For concrete work, you'll need:\n\n✅ Required:\n- Hard hat (CSA Z94.1)\n- Safety glasses w/ side shields\n- Steel-toed boots (CSA Grade 1)\n- High-vis vest (Class 2 min)\n- Rubber boots\n- Chemical-resistant gloves\n\n⚠️ For overhead work: Face shield required\n\nSafety briefing completion required before entering active pour area! 🦺",
          "timestamp": "8:16 AM",
          "sender": "Robin"
        },
        {
          "type": "sent",
          "text": "Perfect. Where do I sign in for the safety briefing?",
          "timestamp": "8:17 AM"
        },
        {
          "type": "received",
          "text": "Site office trailer at Bay entrance. Jennifer Walsh is running the briefing at 8:30 AM for today's Pour #3. All crew must complete it before accessing Bay A-F. See you there! 📋",
          "timestamp": "8:18 AM",
          "sender": "Robin"
        }
      ]
    }`,
    
      // Foundation Pour Timing
      `{
      "messages": [
        {
          "type": "sent",
          "text": "What time does the concrete pour start today?",
          "timestamp": "12:45 PM"
        },
        {
          "type": "received",
          "text": "Foundation Pour #3 starts at 1:45 PM today (Nov 17). Covering Bay A-F. Jennifer Walsh is coordinating. All crew must complete safety briefing and be in position by 1:40 PM. Expected duration: 1 hour. 🚨",
          "timestamp": "12:46 PM",
          "sender": "Robin"
        },
        {
          "type": "sent",
          "text": "Who should I check in with when I arrive?",
          "timestamp": "12:47 PM"
        },
        {
          "type": "received",
          "text": "Check in with Jennifer Walsh (Site Super) at the site office trailer. She's at (204) 555-0182 if you need to reach her before arrival. Concrete vibrators are staged in Bay B tool trailer. Let's make it a smooth pour! 💪",
          "timestamp": "12:48 PM",
          "sender": "Robin"
        }
      ]
    }`
  ];

  const sampleConversation = conversationVariations[0]; // Use Wings & Strings Fest as default

  // Load conversations from JSON files
  useEffect(() => {
    const loadConversations = async () => {
      try {
        // Load the list of conversation files
        const response = await fetch('/conversations/conversations.json');
        const { conversations } = await response.json();
        
        // Load each conversation file
        const loadedConversations = await Promise.all(
          conversations.map(async (filename) => {
            const res = await fetch(`/conversations/${filename}`);
            return await res.json();
          })
        );
        
        // Set the first conversation as default
        if (loadedConversations.length > 0) {
          const firstConvo = loadedConversations[0];
          setConversationData(JSON.stringify(firstConvo, null, 2));
          setParsedMessages(firstConvo.messages || []);
          setEditableConversations(loadedConversations);
        }
      } catch (error) {
        console.error('Failed to load conversations:', error);
        // Fallback to hardcoded conversation if file loading fails
        setConversationData(sampleConversation);
        try {
          const parsed = JSON.parse(sampleConversation);
          setParsedMessages(parsed.messages || []);
        } catch (e) {
          console.error('Failed to parse sample conversation');
        }
        
        const editableConvos = conversationVariations.map(convo => {
          try {
            return JSON.parse(convo);
          } catch (e) {
            return { messages: [] };
          }
        });
        setEditableConversations(editableConvos);
      }
    };
    
    loadConversations();
    
    // Only set default images if nothing is saved in localStorage
    const savedImages = localStorage.getItem('messageScreenGrabImages');
    if (!savedImages || savedImages === '{}') {
      // Set first background as default
      const defaultBackground = availableBackgrounds[0];
      setSelectedBackground(defaultBackground);
      setBackgroundImage(defaultBackground.path);
      
      // Set first foreground as default
      const defaultForeground = availableForegrounds[0];
      setSelectedForeground(defaultForeground);
      setForegroundImage(defaultForeground.path);
    }
    
    // Auto-start animation after a brief delay
    setTimeout(() => {
      setIsAnimating(true);
    }, 500);
    
    // Force initial element positions and rotation to be applied
    setTimeout(() => {
      setElementPositions(prev => ({ 
        ...prev,
        foreground: { ...prev.foreground, rotation: 15 }, // Force the 15° rotation
        background: { ...prev.background, rotation: 0 }
      }));
    }, 100);
  }, []);

  // TypingIndicator component - iOS style 3 dots
  const TypingIndicator = () => (
    <div className="flex items-start justify-start mb-1">
      <div 
        className="relative max-w-[255px] p-3 rounded-[20px] bg-[#E9E9EB] border-[#E9E9EB]"
        style={{
          borderBottomLeftRadius: '6px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, sans-serif'
        }}
      >
        <div className="flex items-center space-x-1">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="w-2 h-2 bg-[#9E9EA1] rounded-full"
              style={{
                animation: `typingDot 1.4s infinite`,
                animationDelay: `${index * 0.2}s`,
                opacity: 0.4
              }}
            />
          ))}
        </div>
        
        {/* iOS-style message tail */}
        <div
          className="absolute bottom-[-1px] left-[-7px] w-5 h-[25px] bg-[#E9E9EB]"
          style={{
            borderBottomRightRadius: '15px 14px'
          }}
        />
        <div
          className="absolute bottom-[-1px] left-[-26px] w-[26px] h-[25px] bg-white"
          style={{
            borderBottomRightRadius: '12px'
          }}
        />
      </div>
    </div>
  );

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settingsToSave = {
      ...settings,
      iphoneBorderColor,
      useIOSBlue,
      palmSwayIntensity,
      palmAnchorPoint,
      botName
      // Note: avatarImage is NOT saved here to avoid quota issues - it's saved separately on upload
    };
    try {
      localStorage.setItem('messageScreenGrabSettings', JSON.stringify(settingsToSave));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.error('LocalStorage quota exceeded. Settings not saved.');
      }
    }
  }, [settings, iphoneBorderColor, useIOSBlue, palmSwayIntensity, palmAnchorPoint, botName]);

  // Save element positions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('messageScreenGrabPositions', JSON.stringify(elementPositions));
  }, [elementPositions]);

  // Save images to localStorage whenever they change
  useEffect(() => {
    const imagesToSave = {
      backgroundImage,
      foregroundImage,
      selectedBackground,
      selectedForeground,
      useCustomBackground,
      useCustomForeground
    };
    localStorage.setItem('messageScreenGrabImages', JSON.stringify(imagesToSave));
  }, [backgroundImage, foregroundImage, selectedBackground, selectedForeground, useCustomBackground, useCustomForeground]);

  // Reset scroll when conversation changes
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = 0;
    }
  }, [parsedMessages]);

  // Handle message animation looping with typing indicators - PROPERLY SEQUENCED
  useEffect(() => {
    if (isAnimating && parsedMessages.length > 0) {
      let currentTime = 0;
      setShowTypingIndicator(false);
      setTypingForMessageIndex(-1);

      // Build the timeline with proper sequencing
      parsedMessages.forEach((message, index) => {
        if (message.type === 'sent') {
          // Sent message just appears, then we wait
          currentTime += settings.messageDelay;
        } else {
          // Received message: Show typing → wait → hide typing → message appears → wait
          
          // 1. Show typing indicator
          const typingStartTime = currentTime;
          setTimeout(() => {
            setShowTypingIndicator(true);
            setTypingForMessageIndex(index);
            // Scroll ONLY if this is the last message AND typing indicator would be cut off
            const isLastMessage = index === parsedMessages.length - 1;
            if (isLastMessage) {
              setTimeout(() => {
                if (messagesRef.current) {
                  const container = messagesRef.current;
                  const currentScrollBottom = container.scrollTop + container.clientHeight;
                  const needsScroll = container.scrollHeight > currentScrollBottom + 60; // 60px buffer for typing indicator
                  
                  if (needsScroll) {
                    container.scrollTo({
                      top: container.scrollHeight - container.clientHeight,
                      behavior: 'smooth'
                    });
                  }
                }
              }, 50); // Small delay to let typing indicator render
            }
          }, typingStartTime);
          
          // 2. Hide typing indicator just before message appears
          const typingEndTime = typingStartTime + settings.typingDuration;
          setTimeout(() => {
            setShowTypingIndicator(false);
            setTypingForMessageIndex(-1);
          }, typingEndTime);
          
          // 3. Message appears (controlled by Framer Motion delay in render)
          const messageAppearTime = typingEndTime + 200; // Small gap after typing disappears
          
          // 4. Scroll ONLY if this is the last message AND message would be cut off
          const isLastMessage = index === parsedMessages.length - 1;
          if (isLastMessage) {
            setTimeout(() => {
              if (messagesRef.current) {
                const container = messagesRef.current;
                const currentScrollBottom = container.scrollTop + container.clientHeight;
                const needsScroll = container.scrollHeight > currentScrollBottom + 100; // Buffer for message
                
                if (needsScroll) {
                  container.scrollTo({
                    top: container.scrollHeight - container.clientHeight,
                    behavior: 'smooth'
                  });
                }
              }
            }, messageAppearTime + 150); // Small delay after message renders
          }
          
          // 5. Advance timeline for next message
          currentTime = messageAppearTime + settings.messageDelay;
        }
      });

      // Show reaction after all messages - give last message double the time
      const reactionTime = currentTime + (settings.messageDelay * 2);
      const reactionTimer = setTimeout(() => {
        setShowReaction(true);
      }, reactionTime);

      // Reset and loop - Give reaction its own dedicated screen time
      const totalAnimationTime = reactionTime + (settings.messageDelay * 2); // Let reaction be visible for 2x message delay
      const loopTimer = setTimeout(() => {
        setMessageAnimationKey(prev => prev + 1);
        setShowTypingIndicator(false);
        setTypingForMessageIndex(-1);
        setShowReaction(false);
        // Reset scroll to top when conversation loops/changes
        if (messagesRef.current) {
          messagesRef.current.scrollTop = 0;
        }
      }, totalAnimationTime);

      return () => {
        clearTimeout(reactionTimer);
        clearTimeout(loopTimer);
      };
    }
  }, [isAnimating, parsedMessages.length, settings.messageDelay, settings.typingDuration, messageAnimationKey]);

  // Camera shake animation with depth simulation
  useEffect(() => {
    if (settings.cameraShake > 0) {
      console.log('▶️ Starting shake animation at', settings.cameraShake, '% intensity');
      const animateShake = () => {
        // Natural camera movement with multiple frequencies
        const time = Date.now() * 0.001; // Convert to seconds
        const intensity = (settings.cameraShake / 100) * 0.3; // Convert 0-100% to 0-0.3 (compressed scale)
        
        // Base camera shake (affects overall scene)
        const baseX = (
          Math.sin(time * 2.1) * 0.7 +
          Math.sin(time * 5.3) * 0.2 +
          Math.sin(time * 8.7) * 0.1
        ) * intensity * 8; // Max 8px at 100%
        
        const baseY = (
          Math.cos(time * 1.8) * 0.6 +
          Math.cos(time * 4.2) * 0.3 +
          Math.cos(time * 7.1) * 0.1
        ) * intensity * 6; // Max 6px at 100%
        
        const rotation = (
          Math.sin(time * 1.5) * 0.8 +
          Math.sin(time * 3.9) * 0.2
        ) * intensity * 1.2; // Max 1.2deg at 100%
        
        setShakeTransform({ x: baseX, y: baseY, rotation });
        
        // Foreground depth-based shake (simulates 3D parallax) - reads current depth in real-time
        const currentDepthMultiplier = 1 + (settings.foregroundDepth / 100) * 8; // 1x to 9x multiplier for dramatic effect
        const foregroundX = baseX * currentDepthMultiplier;
        const foregroundY = baseY * currentDepthMultiplier;
        
                setForegroundShakeTransform({ x: foregroundX, y: foregroundY });
        
        // Update heartbeat to show animation is alive
        setAnimationHeartbeat(Date.now());
        
        // Keep the animation running
        if (settings.cameraShake > 0) {
          shakeAnimationRef.current = requestAnimationFrame(animateShake);
        }
      };
      
      // Start the animation with initial heartbeat
      setAnimationHeartbeat(Date.now());
      shakeAnimationRef.current = requestAnimationFrame(animateShake);
      
            return () => {
        if (shakeAnimationRef.current) {
          cancelAnimationFrame(shakeAnimationRef.current);
          shakeAnimationRef.current = null;
        }
      };
    } else {
      // Reset shake when disabled
      setShakeTransform({ x: 0, y: 0, rotation: 0 });
      setForegroundShakeTransform({ x: 0, y: 0 });
      if (shakeAnimationRef.current) {
        cancelAnimationFrame(shakeAnimationRef.current);
        shakeAnimationRef.current = null;
      }
    }
  }, [settings.cameraShake]); // Remove depth from dependencies to prevent constant restart

  // Animation watchdog - restarts animation if it stops unexpectedly
  useEffect(() => {
    if (settings.cameraShake > 0) {
      const watchdog = setInterval(() => {
        const now = Date.now();
        const timeSinceLastHeartbeat = now - animationHeartbeat;
        
        // If no heartbeat for more than 200ms and animation should be running, restart it
        if (timeSinceLastHeartbeat > 200 && !shakeAnimationRef.current) {
          console.log('🔄 Animation stopped unexpectedly, restarting...');
          setSettings(prev => ({ ...prev, cameraShake: prev.cameraShake }));
        }
      }, 100); // Check every 100ms
      
      return () => clearInterval(watchdog);
    }
  }, [settings.cameraShake, animationHeartbeat]);


  // Palm swaying animation - smooth, constant sway
  useEffect(() => {
    if (palmSwayIntensity > 0) {
      const animatePalmSway = () => {
        const time = Date.now() * 0.001; // Convert to seconds
        const intensity = palmSwayIntensity / 100; // Convert 0-100% to 0-1
        
        // Primary gentle sway - slower, more predictable
        const swayPeriod = 3.5; // 3.5 second cycle for natural palm movement
        const swayPhase = (time % swayPeriod) / swayPeriod; // 0-1 normalized phase
        
        // Use smooth easing function for natural acceleration/deceleration
        const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;
        const easedPhase = easeInOutSine(swayPhase);
        
        // Convert to smooth pendulum motion (-1 to 1)
        const swayAmount = Math.sin(easedPhase * Math.PI * 2);
        
        // Gentle secondary motion for more realism (much slower)
        const secondaryPeriod = 7; // 7 second cycle
        const secondaryPhase = (time % secondaryPeriod) / secondaryPeriod;
        const secondaryAmount = Math.sin(secondaryPhase * Math.PI * 2) * 0.3;
        
        // Combine primary and secondary motion
        const totalSway = (swayAmount + secondaryAmount) * intensity;
        
        // Apply rotation (max 12 degrees at 100% intensity)
        const rotation = totalSway * 12;
        
        // Subtle translation following the sway
        const translateX = totalSway * 6; // Max 6px horizontal movement
        const translateY = Math.abs(totalSway) * 2; // Subtle vertical lift, max 2px
        
        setPalmSwayTransform({ 
          rotation: rotation, 
          x: translateX, 
          y: translateY 
        });
        
        palmSwayAnimationRef.current = requestAnimationFrame(animatePalmSway);
      };
      
      palmSwayAnimationRef.current = requestAnimationFrame(animatePalmSway);
      
      return () => {
        if (palmSwayAnimationRef.current) {
          cancelAnimationFrame(palmSwayAnimationRef.current);
        }
      };
    } else {
      // Reset sway when disabled
      setPalmSwayTransform({ rotation: 0, x: 0, y: 0 });
    }
  }, [palmSwayIntensity]);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowBgDropdown(false);
        setShowFgDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // TypingIndicator component - iOS style 3 dots

  // Cleanup animation frames on unmount
  useEffect(() => {
    return () => {
      if (shakeAnimationRef.current) {
        cancelAnimationFrame(shakeAnimationRef.current);
      }
      if (palmSwayAnimationRef.current) {
        cancelAnimationFrame(palmSwayAnimationRef.current);
      }
      if (anchorAdjustTimeout) {
        clearTimeout(anchorAdjustTimeout);
      }
    };
  }, [anchorAdjustTimeout]);

  // Helper functions for element positioning
  const updateElementPosition = (element, newPosition) => {
    setElementPositions(prev => ({
      ...prev,
      [element]: { ...prev[element], ...newPosition }
    }));
  };



  // Handle conversation data changes
  const handleConversationChange = (value) => {
    setConversationData(value);
    try {
      const parsed = JSON.parse(value);
      setParsedMessages(parsed.messages || []);
    } catch (e) {
      setParsedMessages([]);
    }
  };

  // File upload handlers
  const handleBackgroundUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setBackgroundImage(dataUrl);
        setUseCustomBackground(true);
        setSelectedBackground(null);
        setShowBgDropdown(false);
        
        // Immediately save to localStorage
        const currentImages = JSON.parse(localStorage.getItem('messageScreenGrabImages') || '{}');
        currentImages.backgroundImage = dataUrl;
        currentImages.useCustomBackground = true;
        currentImages.selectedBackground = null;
        localStorage.setItem('messageScreenGrabImages', JSON.stringify(currentImages));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBackgroundSelect = (background) => {
    setSelectedBackground(background);
    setBackgroundImage(background.path);
    setUseCustomBackground(false);
    setShowBgDropdown(false);
  };

  const handleForegroundSelect = (foreground) => {
    setSelectedForeground(foreground);
    setForegroundImage(foreground.path);
    setUseCustomForeground(false);
    setShowFgDropdown(false);
  };

  const handleForegroundUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setForegroundImage(dataUrl);
        setUseCustomForeground(true);
        setSelectedForeground(null);
        setShowFgDropdown(false);
        
        // Immediately save to localStorage
        const currentImages = JSON.parse(localStorage.getItem('messageScreenGrabImages') || '{}');
        currentImages.foregroundImage = dataUrl;
        currentImages.useCustomForeground = true;
        currentImages.selectedForeground = null;
        localStorage.setItem('messageScreenGrabImages', JSON.stringify(currentImages));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        // Open cropper modal
        setAvatarCropImage(dataUrl);
        setShowAvatarCropper(true);
        setCropScale(1);
        setCropPosition({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };
  
  const handleAvatarCropConfirm = () => {
    if (!avatarCropImage || !avatarCropCanvasRef.current) return;
    
    const canvas = avatarCropCanvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size for avatar (128x128 is good for avatars)
    const outputSize = 128;
    canvas.width = outputSize;
    canvas.height = outputSize;
    
    // Create an image from the crop source
    const img = new Image();
    img.onload = () => {
      // Clear canvas
      ctx.clearRect(0, 0, outputSize, outputSize);
      
      // Preview area dimensions
      const previewWidth = 300; // Based on the preview container
      const previewHeight = 300;
      const cropWindowSize = 200; // The circular crop window size
      
      // Calculate how the image fits in the preview (maintaining aspect ratio)
      const imgAspect = img.width / img.height;
      let displayWidth, displayHeight;
      
      if (imgAspect > 1) {
        // Landscape
        displayHeight = previewHeight;
        displayWidth = previewHeight * imgAspect;
      } else {
        // Portrait or square
        displayWidth = previewWidth;
        displayHeight = previewWidth / imgAspect;
      }
      
      // Apply scale
      displayWidth *= cropScale;
      displayHeight *= cropScale;
      
      // Calculate center of preview area
      const previewCenterX = previewWidth / 2;
      const previewCenterY = previewHeight / 2;
      
      // Calculate where the image starts (before drag offset)
      const imageStartX = previewCenterX - (displayWidth / 2);
      const imageStartY = previewCenterY - (displayHeight / 2);
      
      // Calculate the actual image position after drag
      const imageFinalX = imageStartX + cropPosition.x;
      const imageFinalY = imageStartY + cropPosition.y;
      
      // Calculate crop window position in preview (centered)
      const cropWindowX = previewCenterX - (cropWindowSize / 2);
      const cropWindowY = previewCenterY - (cropWindowSize / 2);
      
      // Calculate which part of the image is in the crop window
      // This is the offset from the image's top-left to the crop window's top-left
      const cropOffsetX = cropWindowX - imageFinalX;
      const cropOffsetY = cropWindowY - imageFinalY;
      
      // Convert display coordinates back to source image coordinates
      const scaleRatio = img.width / displayWidth;
      const sourceX = cropOffsetX * scaleRatio;
      const sourceY = cropOffsetY * scaleRatio;
      const sourceSize = cropWindowSize * scaleRatio;
      
      // Draw the cropped portion
      ctx.drawImage(
        img,
        sourceX,                    // Source X
        sourceY,                    // Source Y
        sourceSize,                 // Source Width
        sourceSize,                 // Source Height
        0,                          // Dest X
        0,                          // Dest Y
        outputSize,                 // Dest Width
        outputSize                  // Dest Height
      );
      
      // Get the cropped/resized image as data URL
      const croppedDataUrl = canvas.toDataURL('image/png', 0.9);
      
      // Check resulting size
      const sizeKB = Math.round((croppedDataUrl.length * 3) / 4 / 1024);
      console.log(`Cropped avatar size: ${sizeKB}KB`);
      
      setAvatarImage(croppedDataUrl);
      
      // Save avatar to separate localStorage key
      try {
        localStorage.setItem('messageScreenGrabAvatar', croppedDataUrl);
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          alert('Cropped avatar is still too large. Try zooming in more to reduce file size.');
          console.error('LocalStorage quota exceeded for avatar');
        }
      }
      
      // Close cropper
      setShowAvatarCropper(false);
      setAvatarCropImage(null);
    };
    img.src = avatarCropImage;
  };
  
  const handleAvatarCropCancel = () => {
    setShowAvatarCropper(false);
    setAvatarCropImage(null);
    setCropScale(1);
    setCropPosition({ x: 0, y: 0 });
  };

  // Double-click handler to toggle UI
  const handleDoubleClick = () => {
    setShowUI(!showUI);
  };

  // Conversation editing functions
  const enterEditMode = () => {
    setIsEditMode(true);
    setEditingConversationIndex(0);
    // Load the first conversation for editing
    if (editableConversations.length > 0) {
      setParsedMessages(editableConversations[0].messages || []);
    }
  };

  const exitEditMode = () => {
    setIsEditMode(false);
    // Return to the original conversation display
    const currentConvo = conversationVariations[editingConversationIndex];
    setConversationData(currentConvo);
    try {
      const parsed = JSON.parse(currentConvo);
      setParsedMessages(parsed.messages || []);
    } catch (e) {
      console.error('Failed to parse conversation');
    }
  };

  const saveConversationEdits = () => {
    // Update the conversation variations with the edited data
    const updatedConvo = JSON.stringify({ messages: parsedMessages }, null, 2);
    const updatedVariations = [...conversationVariations];
    updatedVariations[editingConversationIndex] = updatedConvo;
    
    // Update the editable conversations as well
    const updatedEditableConvos = [...editableConversations];
    updatedEditableConvos[editingConversationIndex] = { messages: parsedMessages };
    setEditableConversations(updatedEditableConvos);
    
    setConversationData(updatedConvo);
    setIsEditMode(false);
  };

  const cycleEditingConversation = (direction) => {
    const newIndex = direction === 'next' 
      ? (editingConversationIndex + 1) % editableConversations.length
      : (editingConversationIndex - 1 + editableConversations.length) % editableConversations.length;
    
    setEditingConversationIndex(newIndex);
    setParsedMessages(editableConversations[newIndex].messages || []);
  };

  const updateMessageText = (messageIndex, newText) => {
    const updatedMessages = [...parsedMessages];
    updatedMessages[messageIndex] = { ...updatedMessages[messageIndex], text: newText };
    setParsedMessages(updatedMessages);
  };

  const addMessage = (type = 'received') => {
    const newMessage = {
      type: type,
      text: "New message...",
      timestamp: "Now",
      ...(type === 'received' && { sender: "Robin" })
    };
    setParsedMessages([...parsedMessages, newMessage]);
  };

  const deleteMessage = (messageIndex) => {
    const updatedMessages = parsedMessages.filter((_, index) => index !== messageIndex);
    setParsedMessages(updatedMessages);
  };

  // Handle anchor point visualization
  const handleAnchorPointChange = (newAnchorPoint) => {
    setPalmAnchorPoint(newAnchorPoint);
    setShowAnchorPoint(true);
    
    // Clear existing timeout
    if (anchorAdjustTimeout) {
      clearTimeout(anchorAdjustTimeout);
    }
    
    // Hide anchor point after 2 seconds of no adjustment
    const timeout = setTimeout(() => {
      setShowAnchorPoint(false);
    }, 2000);
    
    setAnchorAdjustTimeout(timeout);
  };

  


  // Export functionality - now supports both image and video
  const exportScreenGrab = async () => {
    setIsExporting(true);
    
    // Create a high-res version for export
    const canvas = exportCanvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set high resolution
    canvas.width = 1170; // iPhone 13 Pro resolution
    canvas.height = 2532;
    
    // Draw background
    if (backgroundImage) {
      const bgImg = new Image();
      bgImg.onload = () => {
        ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
        
        // Draw messages overlay
        drawMessagesOverlay(ctx, canvas.width, canvas.height);
        
        // Download
        const link = document.createElement('a');
        link.download = `message-screengrab-${Date.now()}.png`;
        link.href = canvas.toDataURL();
        link.click();
        
        setIsExporting(false);
      };
      bgImg.src = backgroundImage;
    } else {
      // Draw default background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawMessagesOverlay(ctx, canvas.width, canvas.height);
      
      const link = document.createElement('a');
      link.download = `message-screengrab-${Date.now()}.png`;
      link.href = canvas.toDataURL();
      link.click();
      
      setIsExporting(false);
    }
  };

  const drawMessagesOverlay = (ctx, width, height) => {
    // This would draw the messages overlay on the canvas
    // For now, we'll implement basic text rendering
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.fillRect(0, height * 0.3, width, height * 0.4);
    
    ctx.fillStyle = '#ffffff';
    ctx.font = '24px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Message Conversation', width / 2, height * 0.5);
  };

  return (
    <div className="fixed inset-0 bg-gray-50 overflow-hidden" onDoubleClick={handleDoubleClick}>
      {/* Add CSS keyframes for typing animation */}
      <style>{`
        @keyframes typingDot {
          0%, 60%, 100% {
            opacity: 0.4;
          }
          30% {
            opacity: 1;
          }
        }
      `}</style>
      
      {/* Live Preview Area - Full Screen */}
      <div className="absolute inset-0">
        {/* 3D Scene Container - With live camera shake */}
        <div
          ref={sceneContainerRef}
          className="w-full h-full relative overflow-hidden"
          style={{
            transform: `translate(${shakeTransform.x}px, ${shakeTransform.y}px) rotate(${shakeTransform.rotation}deg)`,
            transformOrigin: 'center center',
            background: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
            backgroundColor: '#fafafa'
          }}
        >
          <div
            className="relative w-full h-full"
            style={{
              perspective: '1000px',
              perspectiveOrigin: 'center center'
            }}
          >
            {/* Background Layer - Optimized for Performance */}
            {backgroundImage && showBackground && (
              <div
                className="absolute inset-0"
                style={{
                  transform: `translate(${elementPositions.background.x}px, ${elementPositions.background.y}px) scale(${settings.backgroundScale * 1.2}) rotate(${elementPositions.background.rotation}deg)`,
                  transformOrigin: 'center center',
                  willChange: 'transform'
                }}
              >
                <motion.div
                  className="w-full h-full cursor-move"
                  drag
                  dragMomentum={false}
                  onDrag={(event, info) => {
                    updateElementPosition('background', {
                      x: elementPositions.background.x + info.delta.x,
                      y: elementPositions.background.y + info.delta.y
                    });
                  }}
                  style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    width: '100%',
                    height: '100%',
                    filter: `blur(${settings.backgroundBlur}px)`
                  }}
                  whileDrag={{ opacity: 0.8 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              </div>
            )}

            {/* iPhone Container - Optimized for Performance */}
            <div
              className="absolute top-1/2 left-1/2 w-96 h-[700px] z-20"
              style={{
                transform: `translate(-50%, -50%) translate(${elementPositions.phone.x}px, ${elementPositions.phone.y}px)`,
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4)) drop-shadow(0 4px 8px rgba(0,0,0,0.25)) drop-shadow(0 12px 30px rgba(0,0,0,0.2))',
                willChange: 'transform'
              }}
            >
              {/* Draggable Wrapper - Separate from Animation */}
              <motion.div
                className="w-full h-full cursor-move"
                drag
                dragMomentum={false}
                onDrag={(event, info) => {
                  updateElementPosition('phone', {
                    x: elementPositions.phone.x + info.delta.x,
                    y: elementPositions.phone.y + info.delta.y
                  });
                }}
                whileDrag={{ opacity: 0.95 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {/* iPhone Frame with Notch - Metallic effect */}
                <div 
                  className="relative w-full h-full rounded-[3rem] p-2" 
                  style={{ 
                    backgroundColor: iphoneBorderColor,
                    boxShadow: `
                      inset 0 0 0 1px rgba(255,255,255,0.2),
                      inset 0 2px 4px rgba(255,255,255,0.15),
                      inset 0 -1px 2px rgba(0,0,0,0.2)
                    `
                  }}
                >
                  {/* iPhone Screen with chamfered edge effect */}
                  <div 
                    className="relative w-full h-full bg-white rounded-[2.5rem] overflow-hidden flex flex-col"
                    style={{
                      boxShadow: `
                        0 0 0 0.5px rgba(0,0,0,0.15),
                        inset 0 0 0 0.5px rgba(255,255,255,0.3)
                      `
                    }}
                  >
                    {/* Realistic iPhone Notch */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-36 h-7 bg-black z-10" style={{
                      borderBottomLeftRadius: '18px',
                      borderBottomRightRadius: '18px'
                    }}>
                      {/* Speaker grill */}
                      <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gray-800 rounded-full opacity-60"></div>
                      {/* Front camera */}
                      <div className="absolute top-1.5 right-4 w-2 h-2 bg-gray-800 rounded-full opacity-80"></div>
                    </div>
                    
                    {/* Status Bar Elements - Positioned beside notch */}
                    <div className="absolute top-0 left-0 right-0 h-8 flex items-center justify-between px-6 pt-2 z-20">
                      {/* Left side - Time */}
                      <div className="flex items-center" style={{ 
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, sans-serif',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        <span style={{ color: '#000000' }}>9:41</span>
                      </div>
                      
                      {/* Right side - Signal, WiFi, Battery */}
                      <div className="flex items-center gap-1">
                        {/* Signal bars */}
                        <div className="flex items-end gap-0.5 mr-1">
                          <div className="w-1 h-1 bg-black rounded-full"></div>
                          <div className="w-1 h-2 bg-black rounded-full"></div>
                          <div className="w-1 h-3 bg-black rounded-full"></div>
                          <div className="w-1 h-4 bg-black rounded-full"></div>
                        </div>
                        {/* Battery icon */}
                        <svg width="24" height="11" viewBox="0 0 24 11" fill="none">
                          <rect opacity="0.35" x="1" y="2" width="20" height="7" rx="2" stroke="black" strokeWidth="1" fill="none"/>
                          <rect x="2" y="3" width="17" height="5" rx="1" fill="black"/>
                          <rect x="21.5" y="4" width="1.5" height="3" rx="0.5" fill="black"/>
                        </svg>
                      </div>
                    </div>
                    
                    {/* Carrier indicator - Below notch */}
                    <div className="h-8 flex items-center justify-center pt-8 flex-shrink-0" style={{ 
                      backgroundColor: '#F2F2F7',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, sans-serif',
                      fontSize: '12px',
                      fontWeight: '400'
                    }}>
                   
                    </div>
                    
                  {/* Chat Header */}
                  <div className="h-16 bg-gray-50 border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0" style={{ 
                    borderBottom: '0.5px solid #C7C7CC',
                    backgroundColor: '#F2F2F7'
                  }}>
                    {/* Left - Back chevron */}
                    <button className="flex items-center justify-center w-8 h-8">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    
                    {/* Center - Avatar and Name */}
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full mb-1 flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#007AFF' }}>
                        <img 
                          src={avatarImage} 
                          alt={botName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to a placeholder if the image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white font-semibold text-sm" style={{ 
                          backgroundColor: '#007AFF',
                          display: 'none'
                        }}>
                          {botName[0]?.toUpperCase() || 'R'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-base" style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, sans-serif',
                          fontSize: '17px',
                          fontWeight: '600',
                          color: '#000000'
                        }}>
                          {botName}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right - Empty space for symmetry */}
                    <div className="w-8 h-8"></div>
                  </div>
                    
                    {/* Messages Area */}
                    <div 
                      ref={messagesRef}
                      className="flex-1 overflow-y-auto pb-6" 
                      style={{ 
                        backgroundColor: '#ffffff',
                        scrollBehavior: 'smooth',
                        minHeight: 0,
                        paddingLeft: '20px',
                        paddingRight: '20px'
                      }}
                    >
                      {/* Top Spacer - Creates space below header */}
                      <div style={{ height: '16px', flexShrink: 0 }}></div>
                      
                      {/* Centered Date - Inside scrollable area */}
                      <div className="flex items-center justify-center" style={{ height: '24px', marginBottom: '12px', flexShrink: 0 }}>
                        <div style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, sans-serif',
                          fontSize: '13px',
                          fontWeight: '400',
                          color: '#8E8E93'
                        }}>
                          Today
                        </div>
                      </div>
                      
                      <div className="flex flex-col space-y-1">
                      {parsedMessages.map((message, index) => {
                        // Check if this message should have a tail (last in sequence from same sender)
                        const isLast = index === parsedMessages.length - 1;
                        const nextMessage = !isLast ? parsedMessages[index + 1] : null;
                        const showTail = isLast || nextMessage?.type !== message.type;
                        
                        // Calculate delay to match the timeline in useEffect
                        let calculatedDelay = 0;
                        if (isAnimating) {
                          // Calculate time up to this message
                          for (let i = 0; i < index; i++) {
                            const prevMessage = parsedMessages[i];
                            if (prevMessage.type === 'sent') {
                              calculatedDelay += settings.messageDelay;
                            } else {
                              // Received: typing duration + gap + message delay
                              calculatedDelay += settings.typingDuration + 200 + settings.messageDelay;
                            }
                          }
                          
                          // If THIS message is received, add typing time + gap before it appears
                          if (message.type === 'received') {
                            calculatedDelay += settings.typingDuration + 200;
                          }
                        }
                        
                        const isLastMessage = index === parsedMessages.length - 1;
                        
                        return (
                          <React.Fragment key={`${messageAnimationKey}-${index}`}>
                            <motion.div
                              className={`flex ${message.type === 'sent' ? 'justify-end' : 'justify-start'}`}
                              style={{ marginBottom: showTail ? '12px' : '2px', position: 'relative' }}
                              initial={{ 
                                opacity: 0, 
                                y: 10,
                                scale: 0.95
                              }}
                              animate={{ 
                                opacity: 1, 
                                y: 0,
                                scale: 1
                              }}
                              transition={{
                                delay: calculatedDelay / 1000,
                                duration: 0.35,
                                ease: [0.25, 0.46, 0.45, 0.94] // iOS-like easing curve
                              }}
                            >
                              <div className={`flex flex-col ${message.type === 'sent' ? 'items-end' : 'items-start'}`} style={{ position: 'relative' }}>
                                <div 
                                  className={`ios-message-bubble ${message.type === 'sent' ? 'sent' : 'received'}`}
                                  style={{
                                    position: 'relative',
                                    maxWidth: '255px',
                                    padding: '8px 16px',
                                    lineHeight: '20px',
                                    wordWrap: 'break-word',
                                    borderRadius: '20px',
                                    fontSize: '16px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, sans-serif',
                                    ...(message.type === 'sent' ? {
                                      backgroundColor: useIOSBlue ? '#007AFF' : '#34C759',
                                      color: 'white',
                                      borderBottomRightRadius: showTail ? '6px' : '20px'
                                    } : {
                                      backgroundColor: '#E9E9EB',
                                      color: 'black',
                                      borderBottomLeftRadius: showTail ? '6px' : '20px'
                                    })
                                  }}
                                >
                                  <div>{message.text}</div>
                                  
                                  {/* iOS-style message tail - only show for last message in sequence */}
                                  {showTail && (
                                    <>
                                      <div
                                        className="ios-tail"
                                        style={{
                                          position: 'absolute',
                                          bottom: '0',
                                          width: '20px',
                                          height: '20px',
                                          overflow: 'hidden',
                                          ...(message.type === 'sent' ? {
                                            right: '-7px',
                                            background: useIOSBlue ? '#007AFF' : '#34C759',
                                            borderBottomLeftRadius: '15px 14px'
                                          } : {
                                            left: '-7px',
                                            background: '#E9E9EB',
                                            borderBottomRightRadius: '15px 14px'
                                          })
                                        }}
                                      />
                                      <div
                                        className="ios-tail-overlay"
                                        style={{
                                          position: 'absolute',
                                          bottom: '0',
                                          width: '20px',
                                          height: '20px',
                                          background: '#ffffff',
                                          overflow: 'hidden',
                                          ...(message.type === 'sent' ? {
                                            right: '-20px',
                                            borderBottomLeftRadius: '10px'
                                          } : {
                                            left: '-20px',
                                            borderBottomRightRadius: '10px'
                                          })
                                        }}
                                      />
                                    </>
                                  )}
                                </div>
                                
                                {/* Delivered status for sent messages */}
                                {message.type === 'sent' && showTail && (
                                  <motion.div
                                    className="text-xs mt-1 mr-2"
                                    style={{
                                      fontSize: '11px',
                                      color: '#8E8E93',
                                      fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, sans-serif'
                                    }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{
                                      delay: isAnimating ? (calculatedDelay / 1000) + 0.3 : 0.3,
                                      duration: 0.3
                                    }}
                                  >
                                    Delivered
                                  </motion.div>
                                )}
                                
                                {/* Reaction - Shows on last message (user reacting to Robin's answer) */}
                                {showReaction && isLastMessage && (
                                  <motion.div
                                    style={{
                                      position: 'absolute',
                                      top: '-16px',
                                      right: '-16px',
                                      zIndex: 50
                                    }}
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    transition={{
                                      duration: 0.3,
                                      ease: [0.25, 0.46, 0.45, 0.94]
                                    }}
                                  >
                                    {/* Reaction bubble with tail - user's message color */}
                                    <div className="relative">
                                      {/* Main reaction circle */}
                                      <div
                                        className="flex items-center justify-center rounded-full"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          backgroundColor: useIOSBlue ? '#007AFF' : '#34C759',
                                          fontSize: '18px'
                                        }}
                                      >
                                        ❤️
                                      </div>
                                      
                                      {/* Small connecting bubble 1 */}
                                      <div
                                        style={{
                                          position: 'absolute',
                                          bottom: '-6px',
                                          right: '8px',
                                          width: '8px',
                                          height: '8px',
                                          backgroundColor: useIOSBlue ? '#007AFF' : '#34C759',
                                          borderRadius: '50%'
                                        }}
                                      />
                                      
                                      {/* Small connecting bubble 2 */}
                                      <div
                                        style={{
                                          position: 'absolute',
                                          bottom: '-12px',
                                          right: '4px',
                                          width: '5px',
                                          height: '5px',
                                          backgroundColor: useIOSBlue ? '#007AFF' : '#34C759',
                                          borderRadius: '50%'
                                        }}
                                      />
                                    </div>
                                  </motion.div>
                                )}
                              </div>
                            </motion.div>
                            
                            {/* Typing Indicator - Shows after this message if next message is being typed */}
                            {showTypingIndicator && typingForMessageIndex === index + 1 && (
                              <motion.div
                                style={{ marginTop: '2px', marginBottom: '2px' }}
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -5, scale: 0.95 }}
                                transition={{
                                  duration: 0.25,
                                  ease: [0.25, 0.46, 0.45, 0.94]
                                }}
                              >
                                <TypingIndicator />
                              </motion.div>
                            )}
                          </React.Fragment>
                        );
                      })}
                      </div>
                    </div>
                  </div>
                  
                  {/* iPhone Home Indicator */}
                  <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-36 h-1.5 bg-white rounded-full opacity-80"></div>
                </div>
              </motion.div>
            </div>

            {/* Foreground Element - Performance Optimized */}
            {foregroundImage && showForeground && (
              <motion.div
                className={`absolute -left-32 -top-32 ${settings.foregroundInFront ? 'z-40' : 'z-10'} ${isAnchorEditMode ? 'cursor-crosshair' : 'cursor-move'}`}
                style={{
                  width: `${600 * settings.foregroundScale}px`,
                  height: `${600 * settings.foregroundScale}px`,
                  transform: `translate3d(${elementPositions.foreground.x + foregroundShakeTransform.x}px, ${elementPositions.foreground.y + foregroundShakeTransform.y}px, 0) rotate(${elementPositions.foreground.rotation}deg)`,
                  willChange: 'transform',
                  // Optimize for GPU rendering
                  backfaceVisibility: 'hidden',
                  perspective: '1000px',
                  // Force initial render with correct rotation
                  transformOrigin: 'center center'
                }}

                                    drag={!isAnchorEditMode}
                dragMomentum={false}
                dragConstraints={false}
                                    onDrag={(event, info) => {
                  // Allow dragging when not in anchor edit mode
                  if (!isAnchorEditMode) {
                    updateElementPosition('foreground', {
                      x: elementPositions.foreground.x + info.delta.x,
                      y: elementPositions.foreground.y + info.delta.y
                    });
                  }
                }}
                onDragEnd={() => {
                  // Ensure animation is still running after drag
                  setTimeout(() => {
                    if (settings.cameraShake > 0 && !shakeAnimationRef.current) {
                      console.log('🔄 Restarting animation after drag');
                      setSettings(prev => ({ ...prev, cameraShake: prev.cameraShake }));
                    }
                  }, 50);
                }}
                
                whileDrag={{ 
                  opacity: 0.9 // Just opacity feedback during drag, no scaling
                }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  >
                {/* Palm Sway Container - Rotates around anchor point */}
                <div
                  className="w-full h-full"
                  style={{
                    transform: `translate3d(${palmSwayTransform.x}px, ${palmSwayTransform.y}px, 0) rotate(${palmSwayTransform.rotation}deg)`,
                    transformOrigin: `${palmAnchorPoint.x}% ${palmAnchorPoint.y}%`,
                    willChange: 'transform'
                  }}
                >
                  <div className="w-full h-full relative">
                    <img
                      src={foregroundImage}
                      alt="Foreground element"
                      className="w-full h-full object-cover relative pointer-events-none"
                      style={{
                        // Apply blur, drop shadow, and opacity
                        filter: `drop-shadow(0 20px 40px rgba(0,0,0,0.2)) blur(${settings.foregroundBlur}px)`,
                        opacity: settings.foregroundOpacity,
                        // GPU optimization
                        transform: 'translateZ(0)',
                        willChange: 'filter'
                      }}
                    />
                    
                    {/* Clickable overlay for anchor point setting - only visible in anchor edit mode */}
                    {isAnchorEditMode && (
                      <div
                        className="absolute inset-0 cursor-crosshair"
                      onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = ((e.clientX - rect.left) / rect.width) * 100;
                          const y = ((e.clientY - rect.top) / rect.height) * 100;
                          handleAnchorPointChange({ x: Math.round(x), y: Math.round(y) });
                      }}
                    />
                    )}
                    
                    {/* Anchor Point Indicator */}
                    {(showAnchorPoint || isAnchorEditMode) && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        transition={{ duration: 0.2 }}
                        className="absolute pointer-events-none z-10"
                        style={{
                          left: `${palmAnchorPoint.x}%`,
                          top: `${palmAnchorPoint.y}%`,
                          transform: 'translate(-50%, -50%)',
                          width: '24px',
                          height: '24px'
                        }}
                      >
                        {/* Modern Anchor Point Design - Matches UI Aesthetic */}
                        <div className="relative w-full h-full">
                          {/* Outer Ring - Blue theme matching UI */}
                          <div 
                            className="absolute inset-0 border-2 rounded-full"
                            style={{
                              borderColor: '#007AFF',
                              background: 'rgba(255, 255, 255, 0.95)',
                              boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3), 0 0 0 1px rgba(0, 122, 255, 0.1)'
                            }}
                          />
                          {/* Inner Dot - Subtle blue */}
                          <div 
                            className="absolute top-1/2 left-1/2 w-1.5 h-1.5 rounded-full"
                            style={{
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: '#007AFF'
                            }}
                          />
                          {/* Subtle Crosshair Lines - Lighter blue */}
                          <div 
                            className="absolute top-1/2 left-1/2 w-3 h-px"
                            style={{
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: '#007AFF',
                              opacity: 0.6
                            }}
                          />
                          <div 
                            className="absolute top-1/2 left-1/2 w-px h-3"
                            style={{
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: '#007AFF',
                              opacity: 0.6
                            }}
                          />
                        </div>
                        
                        {/* Tooltip - Matching UI Style */}
                        <div 
                          className="absolute top-6 left-1/2 transform -translate-x-1/2 px-2 py-1 rounded whitespace-nowrap font-['VT323']"
                          style={{
                            fontSize: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            color: '#007AFF',
                            border: '1px solid rgba(0, 122, 255, 0.2)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          Anchor ({palmAnchorPoint.x}%, {palmAnchorPoint.y}%)
                        </div>
                      </motion.div>
                    )}
                </div>
              </div>
              </motion.div>
            )}
          </div>
        </div>





        {/* Simple UI Panel */}
        {showUI && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute top-4 right-4 z-50 w-80"
          >
            <div className="bg-white/95 backdrop-blur-xl rounded-xl shadow-lg border border-black/[0.08] overflow-hidden max-h-[90vh] flex flex-col">
              
              {/* Header */}
              <div className="bg-gray-100/80 px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={isEditMode ? exitEditMode : onBack}
                    className="flex items-center justify-center w-6 h-6 bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 rounded-full transition-colors"
                  >
                    <FaArrowLeft className="w-3 h-3" />
                  </button>
                  <h1 className="text-sm font-['VT323'] text-black/80">
                    {isEditMode ? 'Edit Conversations' : 'Robin Messages'}
                  </h1>
                </div>
                <button
                  onClick={() => setShowUI(false)}
                  className="flex items-center justify-center w-6 h-6 bg-red-500/10 hover:bg-red-500/20 text-red-600 rounded-full transition-colors"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </div>

              {/* Conditional Content */}
              {isEditMode ? (
                /* Edit Mode UI - Improved Layout */
                <div className="flex flex-col h-full overflow-hidden">
                  {/* Conversation Grid Selector */}
                  <div className="p-4 border-b border-gray-200 bg-white">
                    <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                      {editableConversations.map((convo, idx) => {
                        const firstSentMessage = convo.messages?.find(m => m.type === 'sent');
                        const preview = firstSentMessage?.text || 'Empty conversation';
                        const isSelected = idx === editingConversationIndex;
                        
                        return (
                          <button
                            key={idx}
                            onClick={() => {
                              setEditingConversationIndex(idx);
                              setParsedMessages(convo.messages || []);
                            }}
                            className={`p-2 rounded-lg text-left transition-all ${
                              isSelected 
                                ? 'bg-blue-500 text-white shadow-md' 
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            <div className="font-['VT323'] text-xs font-semibold mb-1">
                              #{idx + 1}
                            </div>
                            <div className={`font-['VT323'] text-xs truncate ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                              {preview.substring(0, 30)}...
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Message Editor - Cleaner Design */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {parsedMessages.map((message, index) => (
                      <div 
                        key={index} 
                        className={`flex gap-3 items-start ${message.type === 'sent' ? 'flex-row-reverse' : ''}`}
                      >
                        {/* Message Badge */}
                        <div className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-['VT323'] font-semibold ${
                          message.type === 'sent' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-green-500 text-white'
                        }`}>
                          {message.type === 'sent' ? 'You' : 'Robin'}
                        </div>

                        {/* Message Content */}
                        <div className="flex-1">
                          <textarea
                            value={message.text}
                            onChange={(e) => updateMessageText(index, e.target.value)}
                            className="w-full p-3 text-sm border-2 border-gray-200 focus:border-blue-400 rounded-lg resize-none font-['VT323'] transition-colors"
                            rows="3"
                            placeholder="Type message..."
                          />
                        </div>

                        {/* Delete Button */}
                        <button
                          onClick={() => deleteMessage(index)}
                          className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 rounded-full transition-colors"
                        >
                          <FaTimes className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Bottom Actions */}
                  <div className="p-4 border-t border-gray-200 space-y-2 bg-white">
                    {/* Add Message Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => addMessage('sent')}
                        className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-['VT323'] transition-colors"
                      >
                        + Add Your Message
                      </button>
                      <button
                        onClick={() => addMessage('received')}
                        className="flex-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-['VT323'] transition-colors"
                      >
                        + Add Robin Message
                      </button>
                    </div>

                    {/* Save Button */}
                    <button
                      onClick={saveConversationEdits}
                      className="w-full px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-['VT323'] text-base transition-colors shadow-md"
                    >
                      💾 Save & Return
                    </button>
                  </div>
                </div>
              ) : (
                /* Regular UI */
                <div className="p-4 space-y-4 overflow-y-auto flex-1">
                  
                  {/* Message Bubble Color */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-['VT323'] text-sm text-black/80">
                        Message Bubble Color
                      </label>
                      <button
                        onClick={() => setUseIOSBlue(!useIOSBlue)}
                        className={`px-3 py-1 rounded font-['VT323'] text-xs transition-colors ${
                          useIOSBlue ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                        }`}
                      >
                        {useIOSBlue ? 'iOS Blue' : 'Green'}
                      </button>
                    </div>
                  </div>
                  
                  {/* iPhone Border Color */}
                  <div>
                    <label className="block font-['VT323'] text-sm text-black/80 mb-2">
                      iPhone Border Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={iphoneBorderColor}
                        onChange={(e) => setIphoneBorderColor(e.target.value)}
                        className="w-12 h-8 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={iphoneBorderColor}
                        onChange={(e) => setIphoneBorderColor(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded font-['VT323'] text-xs"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                  
                  {/* Bot Name Customization */}
                  <div>
                    <label className="block font-['VT323'] text-sm text-black/80 mb-2">
                      Bot Name
                    </label>
                    <input
                      type="text"
                      value={botName}
                      onChange={(e) => setBotName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded font-['VT323'] text-sm"
                      placeholder="Robin"
                    />
                  </div>
                  
                  {/* Avatar Customization */}
                  <div>
                    <label className="block font-['VT323'] text-sm text-black/80 mb-2">
                      Bot Avatar (supports GIF)
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        <img 
                          src={avatarImage} 
                          alt={botName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white font-semibold text-sm" style={{ display: 'none' }}>
                          {botName[0]?.toUpperCase() || 'R'}
                        </div>
                      </div>
                      <button
                        onClick={() => avatarInputRef.current?.click()}
                        className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded font-['VT323'] text-sm transition-colors"
                      >
                        Upload Avatar
                      </button>
                      {avatarImage !== '/goofer.gif' && (
                        <button
                          onClick={() => {
                            setAvatarImage('/goofer.gif');
                            // Clear the saved avatar from localStorage
                            try {
                              localStorage.removeItem('messageScreenGrabAvatar');
                            } catch (e) {
                              console.error('Error clearing avatar:', e);
                            }
                          }}
                          className="px-3 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded font-['VT323'] text-sm transition-colors"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Camera Shake */}
                  <div>
                    <label className="block font-['VT323'] text-sm text-black/80 mb-2">
                      Camera Shake: {settings.cameraShake}% (intensity: {((settings.cameraShake / 100) * 0.3 * 100).toFixed(0)}%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={settings.cameraShake}
                      onChange={(e) => setSettings({...settings, cameraShake: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>

                  {/* Typing Indicator Duration */}
                  <div>
                    <label className="block font-['VT323'] text-sm text-black/80 mb-2">
                      Robin Typing Duration: {(settings.typingDuration / 1000).toFixed(1)}s
                    </label>
                    <input
                      type="range"
                      min="500"
                      max="3000"
                      step="100"
                      value={settings.typingDuration}
                      onChange={(e) => setSettings({...settings, typingDuration: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>

                  {/* Background & Foreground Controls - Side by Side */}
                  <div className="grid grid-cols-2 gap-4">
                    
                    {/* Background Column */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="font-['VT323'] text-sm text-black/80">Background</label>
                        <button
                          onClick={() => setShowBackground(!showBackground)}
                          className={`px-2 py-1 rounded font-['VT323'] text-xs transition-colors ${
                            showBackground ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                          }`}
                        >
                          {showBackground ? 'ON' : 'OFF'}
                        </button>
                      </div>
                      
                      {/* Background Dropdown */}
                      <div className="relative dropdown-container">
                        <button
                          onClick={() => setShowBgDropdown(!showBgDropdown)}
                          className="w-full flex items-center justify-between p-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {selectedBackground ? (
                              <>
                                <img src={selectedBackground.path} alt={selectedBackground.name} className="w-6 h-6 object-cover rounded" />
                                <span className="font-['VT323'] text-sm">{selectedBackground.name}</span>
                              </>
                            ) : useCustomBackground ? (
                              <>
                                <img src={backgroundImage} alt="Custom" className="w-6 h-6 object-cover rounded" />
                                <span className="font-['VT323'] text-sm">Custom</span>
                              </>
                            ) : (
                              <span className="font-['VT323'] text-sm text-gray-500">Select Background</span>
                            )}
                          </div>
                          <span className="text-gray-400">▼</span>
                        </button>
                        
                        {showBgDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                            {availableBackgrounds.map((bg) => (
                              <button
                                key={bg.name}
                                onClick={() => handleBackgroundSelect(bg)}
                                className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 transition-colors first:rounded-t-lg"
                              >
                                <img src={bg.path} alt={bg.name} className="w-6 h-6 object-cover rounded" />
                                <span className="font-['VT323'] text-sm">{bg.name}</span>
                              </button>
                            ))}
                            <button
                              onClick={() => {
                                bgInputRef.current?.click();
                                setShowBgDropdown(false);
                              }}
                              className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 transition-colors border-t border-gray-200 rounded-b-lg"
                            >
                              <FaUpload className="w-4 h-4 text-gray-400" />
                              <span className="font-['VT323'] text-sm">Upload Custom</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Background Scale Control */}
                      {backgroundImage && (
                        <div className="mt-3">
                          <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                            Scale: {settings.backgroundScale.toFixed(1)}x
                          </label>
                          <input
                            type="range"
                            min="0.5"
                            max="3"
                            step="0.1"
                            value={settings.backgroundScale}
                            onChange={(e) => {
                              // Immediate update for smoother scaling
                              const newScale = parseFloat(e.target.value);
                              setSettings(prev => ({
                                ...prev,
                                backgroundScale: newScale
                              }));
                            }}
                            className="w-full"
                          />
                        </div>
                      )}
                      
                      {/* Background Rotation Control */}
                      {backgroundImage && (
                        <div className="mt-3">
                                                     <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                             Rotation: {elementPositions.background.rotation}°
                             <span className="text-green-600 ml-1">✓</span>
                           </label>
                          <input
                            type="range"
                            min="-180"
                            max="180"
                            step="5"
                            value={elementPositions.background.rotation}
                            onChange={(e) => {
                              updateElementPosition('background', {
                                rotation: parseInt(e.target.value)
                              });
                            }}
                            className="w-full"
                          />
                        </div>
                      )}
                      
                      {/* Background Blur Control */}
                      {backgroundImage && (
                        <div className="mt-3">
                          <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                            Blur: {settings.backgroundBlur}px
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="20"
                            step="1"
                            value={settings.backgroundBlur}
                            onChange={(e) => {
                              const newBlur = parseInt(e.target.value);
                              setSettings(prev => ({
                                ...prev,
                                backgroundBlur: newBlur
                              }));
                            }}
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>

                    {/* Foreground Column */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="font-['VT323'] text-sm text-black/80">Foreground</label>
                        <button
                          onClick={() => setShowForeground(!showForeground)}
                          className={`px-2 py-1 rounded font-['VT323'] text-xs transition-colors ${
                            showForeground ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                          }`}
                        >
                          {showForeground ? 'ON' : 'OFF'}
                        </button>
                      </div>
                      
                      {/* Foreground Dropdown */}
                      <div className="relative dropdown-container">
                        <button
                          onClick={() => setShowFgDropdown(!showFgDropdown)}
                          className="w-full flex items-center justify-between p-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {selectedForeground ? (
                              <>
                                <img src={selectedForeground.path} alt={selectedForeground.name} className="w-6 h-6 object-cover rounded" />
                                <span className="font-['VT323'] text-sm">{selectedForeground.name}</span>
                              </>
                            ) : useCustomForeground ? (
                              <>
                                <img src={foregroundImage} alt="Custom" className="w-6 h-6 object-cover rounded" />
                                <span className="font-['VT323'] text-sm">Custom</span>
                              </>
                            ) : (
                              <span className="font-['VT323'] text-sm text-gray-500">Select Foreground</span>
                            )}
                          </div>
                          <span className="text-gray-400">▼</span>
                        </button>
                        
                        {showFgDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                            {availableForegrounds.map((fg) => (
                              <button
                                key={fg.name}
                                onClick={() => handleForegroundSelect(fg)}
                                className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 transition-colors first:rounded-t-lg"
                              >
                                <img src={fg.path} alt={fg.name} className="w-6 h-6 object-cover rounded" />
                                <span className="font-['VT323'] text-sm">{fg.name}</span>
                              </button>
                            ))}
                            <button
                              onClick={() => {
                                fgInputRef.current?.click();
                                setShowFgDropdown(false);
                              }}
                              className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 transition-colors border-t border-gray-200 rounded-b-lg"
                            >
                              <FaUpload className="w-4 h-4 text-gray-400" />
                              <span className="font-['VT323'] text-sm">Upload Custom</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Foreground Controls */}
                      {foregroundImage && (
                        <>
                          <div className="mt-3">
                            <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                              Scale: {settings.foregroundScale.toFixed(1)}x ({Math.round(600 * settings.foregroundScale)}px)
                            </label>
                            <input
                              type="range"
                              min="0.5"
                              max="5"
                              step="0.1"
                              value={settings.foregroundScale}
                              onChange={(e) => {
                                // Immediate update for smoother scaling
                                const newScale = parseFloat(e.target.value);
                                setSettings(prev => ({
                                  ...prev,
                                  foregroundScale: newScale
                                }));
                              }}
                              className="w-full"
                            />
                          </div>
                          
                          <div className="mt-3">
                            <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                              Blur: {settings.foregroundBlur}px
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="20"
                              step="1"
                              value={settings.foregroundBlur}
                              onChange={(e) => {
                                // Immediate update for smoother blur adjustment
                                const newBlur = parseInt(e.target.value);
                                setSettings(prev => ({
                                  ...prev,
                                  foregroundBlur: newBlur
                                }));
                              }}
                              className="w-full"
                            />
                          </div>
                          
                          <div className="mt-3">
                            <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                              Opacity: {Math.round(settings.foregroundOpacity * 100)}%
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={settings.foregroundOpacity}
                              onChange={(e) => {
                                const newOpacity = parseFloat(e.target.value);
                                setSettings(prev => ({
                                  ...prev,
                                  foregroundOpacity: newOpacity
                                }));
                              }}
                              className="w-full"
                            />
                          </div>
                          
                          <div className="mt-3">
                            <label className="flex items-center justify-between font-['VT323'] text-xs text-black/80 mb-1">
                              <span>Layer Position</span>
                              <span className="text-black/60">{settings.foregroundInFront ? 'In Front' : 'Behind'}</span>
                            </label>
                            <button
                              onClick={() => {
                                setSettings(prev => ({
                                  ...prev,
                                  foregroundInFront: !prev.foregroundInFront
                                }));
                              }}
                              className={`w-full py-2 px-3 rounded-lg font-['VT323'] text-sm transition-colors ${
                                settings.foregroundInFront 
                                  ? 'bg-green-500 text-white hover:bg-green-600' 
                                  : 'bg-gray-300 text-black/70 hover:bg-gray-400'
                              }`}
                            >
                              {settings.foregroundInFront ? '🌴 In Front of Phone' : '📱 Behind Phone'}
                            </button>
                          </div>
                          
                          <div className="mt-3">
                            <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                              Rotation: {elementPositions.foreground.rotation}° 
                              <span className="text-green-600 ml-1">✓</span>
                            </label>
                            <input
                              type="range"
                              min="-180"
                              max="180"
                              step="5"
                              value={elementPositions.foreground.rotation}
                              onChange={(e) => {
                                updateElementPosition('foreground', {
                                  rotation: parseInt(e.target.value)
                                });
                              }}
                              className="w-full"
                            />
                          </div>
                          
                          <div className="mt-3">
                                <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                              Depth (3D Parallax): {settings.foregroundDepth}%
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  step="5"
                              value={settings.foregroundDepth}
                              onChange={(e) => {
                                const newDepth = parseInt(e.target.value);
                                setSettings(prev => ({
                                  ...prev,
                                  foregroundDepth: newDepth
                                }));
                              }}
                                  className="w-full"
                                />
                            <div className="font-['VT323'] text-xs text-gray-500 mt-1">
                              <div>Higher depth = more camera shake movement</div>
                              {settings.foregroundDepth > 0 && settings.cameraShake > 0 && (
                                <div className="text-blue-600 mt-1">
                                  Active: {(1 + (settings.foregroundDepth / 100) * 8).toFixed(1)}x shake
                              </div>
                              )}
                                                             <div className="text-xs text-gray-400 mt-1">
                                 Parallax: {shakeAnimationRef.current && settings.cameraShake > 0 ? 
                                   <span className="text-green-500">● ACTIVE</span> : 
                                   <span className="text-red-500">○ STOPPED</span>
                                 }
                                 <span className="ml-2 text-xs">
                                   (Last: {animationHeartbeat ? new Date(animationHeartbeat).toLocaleTimeString().split(' ')[0] : 'Never'})
                                 </span>
                               </div>
                            </div>
                          </div>
                          
                          <div className="mt-3">
                                <label className="block font-['VT323'] text-xs text-black/80 mb-1">
                              Palm Sway: {palmSwayIntensity}%
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  step="5"
                              value={palmSwayIntensity}
                              onChange={(e) => setPalmSwayIntensity(parseInt(e.target.value))}
                                  className="w-full"
                                />
                              </div>
                          
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-['VT323'] text-xs text-gray-600">
                                Anchor Point ({palmAnchorPoint.x}%, {palmAnchorPoint.y}%)
                              </span>
                              <button
                                onClick={() => setIsAnchorEditMode(!isAnchorEditMode)}
                                className={`px-2 py-1 rounded font-['VT323'] text-xs transition-colors ${
                                  isAnchorEditMode 
                                    ? 'bg-blue-500 text-white' 
                                    : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                                }`}
                              >
                                {isAnchorEditMode ? 'Exit Edit' : 'Edit Anchor'}
                              </button>
                            </div>
                            {isAnchorEditMode && (
                              <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                                <p className="font-['VT323'] text-xs text-blue-700">
                                  Click on the palm image to set anchor point
                                </p>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Conversation Controls */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-['VT323'] text-sm text-black/80">Conversation</label>
                      <div className="flex gap-2">
                        <button
                          onClick={enterEditMode}
                          className="px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs font-['VT323'] transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            const currentIndex = conversationVariations.indexOf(conversationData);
                            const nextIndex = (currentIndex + 1) % conversationVariations.length;
                            const nextConvo = conversationVariations[nextIndex];
                            setConversationData(nextConvo);
                            handleConversationChange(nextConvo);
                          }}
                          className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-['VT323'] transition-colors"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => {
                            const randomIndex = Math.floor(Math.random() * conversationVariations.length);
                            const randomConvo = conversationVariations[randomIndex];
                            setConversationData(randomConvo);
                            handleConversationChange(randomConvo);
                          }}
                          className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-['VT323'] transition-colors"
                        >
                          Random
                        </button>
                      </div>
                    </div>
                    {parsedMessages.length > 0 && (
                      <p className="font-['VT323'] text-xs text-green-600">
                        ✓ {parsedMessages.length} messages loaded
                        {conversationVariations.includes(conversationData) && (
                          <span className="ml-2 text-blue-600">
                            (Robin #{conversationVariations.indexOf(conversationData) + 1})
                          </span>
                        )}
                      </p>
                    )}
                  </div>

                  {/* Instructions */}
                  <div className="space-y-2">
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="font-['VT323'] text-xs text-blue-700">
                        💡 Double-click anywhere to show this panel<br/>
                        Drag iPhone and images to position them
                      </p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="font-['VT323'] text-xs text-green-700">
                        🎥 Use screen capture tools (QuickTime, OBS, etc.)<br/>
                        to record video footage for your video edits
                      </p>
                    </div>
                  </div>

                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Hidden File Inputs */}
        <input
          ref={bgInputRef}
          type="file"
          accept="image/*"
          onChange={handleBackgroundUpload}
          className="hidden"
        />
        <input
          ref={fgInputRef}
          type="file"
          accept="image/*"
          onChange={handleForegroundUpload}
          className="hidden"
        />
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*,image/gif"
          onChange={handleAvatarUpload}
          className="hidden"
        />

        {/* Avatar Cropper Modal */}
        {showAvatarCropper && avatarCropImage && (
          <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80"
            onClick={(e) => {
              if (e.target === e.currentTarget) handleAvatarCropCancel();
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-['VT323'] text-2xl text-black mb-4">Crop Avatar</h3>
              
              {/* Crop Preview Area */}
              <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4" style={{ width: '300px', height: '300px', margin: '0 auto' }}>
                {/* Crop Window (circular) */}
                <div 
                  className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
                >
                  <div className="relative" style={{ width: '200px', height: '200px' }}>
                    {/* Circular mask overlay */}
                    <div 
                      className="absolute inset-0 rounded-full border-4 border-white shadow-lg"
                      style={{ 
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
                      }}
                    />
                  </div>
                </div>
                
                {/* Draggable Image */}
                <motion.div
                  drag
                  dragMomentum={false}
                  onDrag={(e, info) => {
                    setCropPosition({
                      x: cropPosition.x + info.delta.x,
                      y: cropPosition.y + info.delta.y
                    });
                  }}
                  className="absolute inset-0 flex items-center justify-center cursor-move"
                  style={{ touchAction: 'none' }}
                >
                  <img
                    src={avatarCropImage}
                    alt="Crop preview"
                    className="max-w-none"
                    style={{
                      transform: `translate(${cropPosition.x}px, ${cropPosition.y}px) scale(${cropScale})`,
                      maxHeight: '300px',
                      maxWidth: '300px'
                    }}
                    draggable={false}
                  />
                </motion.div>
              </div>
              
              {/* Zoom Control */}
              <div className="mb-6">
                <label className="block font-['VT323'] text-sm text-black/80 mb-2">
                  Zoom: {cropScale.toFixed(1)}x
                </label>
                <div className="flex items-center gap-3">
                  <span className="font-['VT323'] text-xs text-gray-500">-</span>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    value={cropScale}
                    onChange={(e) => setCropScale(parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <span className="font-['VT323'] text-xs text-gray-500">+</span>
                </div>
              </div>
              
              {/* Instructions */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="font-['VT323'] text-xs text-blue-700">
                  💡 Drag the image to position it<br/>
                  Use the zoom slider to resize<br/>
                  Image will be cropped to the circle
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleAvatarCropCancel}
                  className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded font-['VT323'] text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setCropPosition({ x: 0, y: 0 });
                    setCropScale(1);
                  }}
                  className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded font-['VT323'] text-sm transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={handleAvatarCropConfirm}
                  className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded font-['VT323'] text-sm transition-colors"
                >
                  Apply
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Hidden Canvas for Avatar Cropping */}
        <canvas
          ref={avatarCropCanvasRef}
          style={{ display: 'none' }}
          width="128"
          height="128"
        />

        {/* Hidden Canvas for Export */}
        <canvas
          ref={exportCanvasRef}
          style={{ display: 'none' }}
          width="1170"
          height="2532"
        />
      </div>
    </div>
  );
};

export default MessageScreenGrab; 